import React, { useMemo, useState } from "react";

import moment from "moment";

import { checkFeatureFlag, useGetDataSubmissionDates, useGetFarmReportingYear, useGetFarms, useTranslation } from "utils";

import { Button, Flex, Icon, Progress } from "components";

import { DataSubmissionBarConfirmationModal } from "pages/app/all-field-view/components/DataSubmissionBarConfirmationModal";
import { useSubmissionStatus } from "pages/app/all-field-view/hooks/useSubmissionStatus";

type DataSubmissionBarProps = {
  enrolledFieldsCount: number;
  fieldsWithMissingTasksCount: number;
  openTasksCount: number;
  outstandingRedzoneCount: number;
  hasMissingDocuments: boolean;
  onOpenTasksClick?: () => void;
  onSubmitClick?: () => Promise<void>;
};

const PROGRESS_CONFIG = {
  SIZE: 72,
  STROKE_WIDTH: 8,
  PROGRESS_CIRCLE_STROKE_COLOR: "#065c45", // variables.$green-400
} as const;

enum DATA_SUBMISSION_BAR_STATUS {
  NOT_STARTED = "notStarted",
  IN_PROGRESS = "inProgress",
  SUBMISSION_OPEN = "submissionOpen",
  SUBMISSION_OVERDUE = "submissionOverdue",
  SUBMISSION_READY = "submissionReady",
  SUBMISSION_REOPENED = "submissionReopened",
  SUBMISSION_REOPENED_OVERDUE = "submissionReopenedOverdue",
  SUBMISSION_REOPENED_READY = "submissionReopenedReady",
  SUBMISSION_COMPLETED = "submissionCompleted",
}

const calculateProgressPercent = (
  enrolledFieldsCount: number,
  fieldsWithMissingTasksCount: number,
  hasMissingDocuments: boolean,
  outstandingRedzoneCount: number
): number => {
  let cumulativeProgress = 0;

  if (!outstandingRedzoneCount) {
    cumulativeProgress += 10;
  }

  if (!hasMissingDocuments) {
    cumulativeProgress += 10;
  }

  const completedFields = enrolledFieldsCount - fieldsWithMissingTasksCount;
  const completionRatio = completedFields / enrolledFieldsCount;

  const fieldProgress = Math.round((Number.isNaN(completionRatio) ? 1 : completionRatio) * 80);

  return Math.ceil(cumulativeProgress + fieldProgress);
};

export const DataSubmissionBar = ({
  enrolledFieldsCount,
  fieldsWithMissingTasksCount,
  openTasksCount,
  outstandingRedzoneCount,
  hasMissingDocuments,
  onOpenTasksClick,
  onSubmitClick,
}: DataSubmissionBarProps) => {
  const { t } = useTranslation("allFieldView");
  const { currentFarm } = useGetFarms();
  const { data: activeFarmReportingYear } = useGetFarmReportingYear(currentFarm?.id || null);
  const { data: dataSubmissionDates } = useGetDataSubmissionDates();

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isRedzonesFeatureFlagEnabled = checkFeatureFlag("redzones"); // TODO: remove after SO-778 is implemented
  const isMissingDocumentsFeatureFlagEnabled = checkFeatureFlag("missingDocuments"); // TODO: remove after SO-778 is implemented

  const { isDataSubmitted, isDataSubmissionReopened } = useSubmissionStatus(activeFarmReportingYear);

  // TODO: remove this line after we merge SO-920 to prod. This is a trick to not remove the old translation keys when merging to dev.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const unusedTranslation = `${t("submissionBar.title.reopened")} ${t("submissionBar.title.standard")}, ${t("submissionBar.incomplete")}, ${t(
    "submissionBar.reopenedMessage"
  )}, ${t("submissionBar.reopenedMessage_other")}, ${t("submissionBar.overdueMessage")}, ${t("submissionBar.overdueMessage_other")}, ${t(
    "submissionBar.complete"
  )}`;

  const isProgramEnabled = useMemo(() => currentFarm?.carbonProgram?.isEnabled ?? false, [currentFarm?.carbonProgram?.isEnabled]);

  const today = moment();
  const dueDate = moment(dataSubmissionDates?.submissionDueDate);

  const isSubmissionPeriodOpen = dataSubmissionDates?.isSubmissionPeriodOpen;

  const isOverdue = useMemo(() => isSubmissionPeriodOpen && dueDate.isValid() && today.isSameOrAfter(dueDate, "day"), [isSubmissionPeriodOpen, today, dueDate]);
  const shouldShowBar = isProgramEnabled && enrolledFieldsCount > 0;

  const progressPercent = useMemo(() => {
    const outstandingRedzoneCountFeatureFlagged = isRedzonesFeatureFlagEnabled ? outstandingRedzoneCount : 0;
    const hasMissingDocumentsFeatureFlagged = isMissingDocumentsFeatureFlagEnabled ? hasMissingDocuments : false;
    return calculateProgressPercent(enrolledFieldsCount, fieldsWithMissingTasksCount, hasMissingDocumentsFeatureFlagged, outstandingRedzoneCountFeatureFlagged);
  }, [
    enrolledFieldsCount,
    fieldsWithMissingTasksCount,
    hasMissingDocuments,
    isMissingDocumentsFeatureFlagEnabled,
    isRedzonesFeatureFlagEnabled,
    outstandingRedzoneCount,
  ]);

  const totalOpenTasks =
    (outstandingRedzoneCount && isRedzonesFeatureFlagEnabled ? 1 : 0) + (hasMissingDocuments && isMissingDocumentsFeatureFlagEnabled ? 1 : 0) + openTasksCount;

  const barStatus = useMemo(() => {
    if (!isSubmissionPeriodOpen) {
      if (progressPercent === 0) {
        return DATA_SUBMISSION_BAR_STATUS.NOT_STARTED;
      }
      return DATA_SUBMISSION_BAR_STATUS.IN_PROGRESS;
    }

    if (isDataSubmitted && !isDataSubmissionReopened) {
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED;
    }

    if (progressPercent === 100) {
      if (isDataSubmissionReopened) {
        return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_REOPENED_READY;
      }
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_READY;
    }

    if (isOverdue) {
      if (isDataSubmissionReopened) {
        return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_REOPENED_OVERDUE;
      }
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_OVERDUE;
    }

    if (isDataSubmissionReopened) {
      return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_REOPENED;
    }

    return DATA_SUBMISSION_BAR_STATUS.SUBMISSION_OPEN;
  }, [isSubmissionPeriodOpen, progressPercent, isDataSubmitted, isDataSubmissionReopened, isOverdue]);

  const showRedText = isOverdue && barStatus !== DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED;

  if (!shouldShowBar || !activeFarmReportingYear) {
    return null;
  }

  return (
    <div className="data-submission-bar">
      <Flex vertical gap="1rem" className="submission-bar-content">
        <Flex align="center" justify="space-between" gap="2rem">
          <div className="progress-circle-container">
            <Progress
              type="circle"
              percent={progressPercent}
              size={PROGRESS_CONFIG.SIZE}
              strokeWidth={PROGRESS_CONFIG.STROKE_WIDTH}
              strokeColor={PROGRESS_CONFIG.PROGRESS_CIRCLE_STROKE_COLOR}
            />
          </div>
          <Flex vertical style={{ flex: 1 }}>
            <p className="paragraph-l-bold margin-0">{t(`submissionBar.status.${barStatus}.title`, { year: activeFarmReportingYear })}</p>
            <p className={`paragraph-m margin-0 ${showRedText ? "red-text" : ""}`}>
              {t(`submissionBar.status.${barStatus}.description`, { count: openTasksCount, year: activeFarmReportingYear })}
            </p>
          </Flex>
          <Flex gap="1.5rem" align="center" className="data-submission-bar-actions-container">
            {isSubmissionPeriodOpen && barStatus !== DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED && (
              <Flex gap="0.5rem" align="center" className="data-submission-bar-time-left-container">
                <Icon iconName="tabler/calendar-time" className={`data-submission-bar-icon ${showRedText ? "red-text" : ""}`} />
                <p className={`data-submission-bar-time-left-text ${showRedText ? "red-text" : ""}`}>
                  {isOverdue ? t("submissionBar.overdue") : t("submissionBar.due", { dueDate: dueDate.format("MMM DD") })}
                </p>
              </Flex>
            )}
            <Flex gap="1rem" align="center">
              {!!totalOpenTasks && isRedzonesFeatureFlagEnabled && (
                <Button type="primary" onClick={onOpenTasksClick} data-testid="open-tasks-cta">
                  {t("submissionBar.viewTasks")} ({totalOpenTasks})
                </Button>
              )}
              {/* TODO: we don't have a rollover to next year functionality yet, so we don't show the action button after the submission is completed */}
              {isSubmissionPeriodOpen && barStatus !== DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED && (
                <Button type="primary" disabled={!!totalOpenTasks || isSubmitting} onClick={() => setShowConfirmationModal(true)} data-testid="submit-data-cta">
                  {/* TODO: remove ts-expect-error after we have a rollover to next year functionality */}
                  {/* @ts-expect-error - we don't have a rollover to next year functionality yet, so we don't show the action button after the submission is completed */}
                  {barStatus === DATA_SUBMISSION_BAR_STATUS.SUBMISSION_COMPLETED ? t("submissionBar.startNextYear") : t("submissionBar.submit")}
                </Button>
              )}
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <DataSubmissionBarConfirmationModal
        showConfirmationModal={showConfirmationModal}
        setShowConfirmationModal={setShowConfirmationModal}
        onSubmitClick={onSubmitClick}
        isSubmitting={isSubmitting}
        setIsSubmitting={setIsSubmitting}
      />
    </div>
  );
};
